import {
  type PoolTone,
  poolBalanceText,
  poolChipText,
  poolMonthName,
  poolTone,
  poolUsageLine,
  poolUsedText,
  type ServicePoolUsage,
} from '@fos/shared/contracts/services';
import { Timer } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { type Access, useAccess } from '../../../lib/useAccess';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { StatusPill } from '../../../ui/StatusPill';

/**
 * Pula godzin usługi (DECISIONS 2026-10-17 [P] „Zgłoszenia z utrzymania liczą się do puli godzin”): godziny na miesiąc
 * kalendarzowy (Europe/Warsaw), wykorzystanie z wpisów czasu z usługą. Kolor niesie znaczenie: neutralny pasek,
 * bursztyn od 80%, czerwień ponad 100% z „ponad pulę: X”. Same godziny — bez kwot (także bez „Widzi stawki i kwoty”).
 * Dane: `services.pools` (zakres klienta i moduł sprawdza serwer; poza zakresem — pusta lista, bez błędu).
 */

const BAR: Record<PoolTone, string> = { neutral: 'bg-accent', warning: 'bg-warning', danger: 'bg-danger' };
const TEXT: Record<PoolTone, string> = { neutral: 'text-fg-secondary', warning: 'text-warning', danger: 'text-danger' };
const PILL: Record<PoolTone, 'neutral' | 'waiting' | 'danger'> = {
  neutral: 'neutral',
  warning: 'waiting',
  danger: 'danger',
};

/** Moduły, w których pula się pokazuje (jak `canSeeServicePools` na serwerze). */
export function canSeePools(access: Access): boolean {
  return (['projects', 'maintenance', 'tasks', 'time', 'clients'] as const).some((m) => access.can(m));
}

/** Pule aktywnych usług klienta (najstarsza usługa pierwsza). */
export function useClientPools(clientId: string, enabled = true) {
  const access = useAccess();
  return trpc.services.pools.useQuery({ clientId }, { enabled: enabled && canSeePools(access) });
}

/** Pula jednej usługi (karta zadania, karta zgłoszenia na tablicy); `null` = bez usługi albo bez aktywnej puli. */
export function useServicePool(serviceId: string | null | undefined): ServicePoolUsage | null {
  const access = useAccess();
  const enabled = Boolean(serviceId) && canSeePools(access);
  const query = trpc.services.pools.useQuery({ serviceIds: serviceId ? [serviceId] : [] }, { enabled });
  return enabled ? (query.data?.find((p) => p.serviceId === serviceId) ?? null) : null;
}

/** Pasek wykorzystania: wartość 0–1 (ponad pulę = pełny, czerwony). */
export function PoolProgress({
  usage,
  label,
  className,
}: {
  usage: ServicePoolUsage;
  label: string;
  className?: string;
}) {
  const tone = poolTone(usage);
  const ratio = usage.poolSeconds > 0 ? Math.min(1, usage.usedSeconds / usage.poolSeconds) : 0;
  return (
    <div
      className={cn('h-1 w-full overflow-hidden rounded-[2px] bg-elevated', className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(ratio * 100)}
      aria-label={label}
    >
      <div className={cn('h-full rounded-[2px]', BAR[tone])} style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

/** Tekst stanu w kolorze znaczenia: „zostało 1 h 40 min” / „ponad pulę: 20 min”. */
export function PoolBalance({ usage, className }: { usage: ServicePoolUsage; className?: string }) {
  return <span className={cn(TEXT[poolTone(usage)], className)}>{poolBalanceText(usage)}</span>;
}

/** Chip „Pula: zostało 1 h 40 min” (karta zadania, karta zgłoszenia); bursztyn od 80%, czerwień ponad pulę. */
export function PoolChip({
  usage,
  size = 'm',
  className,
}: {
  usage: ServicePoolUsage;
  size?: 's' | 'm';
  className?: string;
}) {
  return (
    <StatusPill
      tone={PILL[poolTone(usage)]}
      icon={Timer}
      size={size}
      className={className}
      title={`${usage.serviceName}: ${poolUsageLine(usage)}`}
    >
      {poolChipText(usage)}
    </StatusPill>
  );
}

/** Wiersz puli usługi: nazwa, „1 h 20 min z 3 h”, pasek i „październik · zostało 1 h 40 min”. */
export function ServicePoolRow({ usage, showName = true }: { usage: ServicePoolUsage; showName?: boolean }) {
  const line = poolUsageLine(usage);
  return (
    <div className="border-b border-line py-3">
      <div className="flex min-w-0 items-baseline gap-3">
        {showName && <span className="min-w-0 truncate text-body-s font-medium text-fg">{usage.serviceName}</span>}
        <span className="flex-1" />
        <span className="shrink-0 text-body-s font-medium text-fg tabular">{poolUsedText(usage)}</span>
      </div>
      <PoolProgress usage={usage} label={`Pula ${usage.serviceName}: ${line}`} className="mt-2" />
      <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-caption">
        <span className="text-fg-muted">{poolMonthName(usage.month)}</span>
        <span aria-hidden className="text-fg-muted">
          ·
        </span>
        <PoolBalance usage={usage} />
      </div>
    </div>
  );
}

/**
 * „Pula godzin” w zakładce Utrzymanie profilu klienta: aktywne usługi z pulą w bieżącym miesiącu. Bez puli albo
 * poza zakresem — nic (sekcja się nie pokazuje).
 */
export function ClientPoolSection({ clientId }: { clientId: string }) {
  const query = useClientPools(clientId);
  const pools = query.data ?? [];
  if (pools.length === 0) return null;
  return (
    <Section aria-labelledby="client-pools">
      <SectionHeader tone="strong" title={<span id="client-pools">Pula godzin</span>} />
      {pools.map((p) => (
        <ServicePoolRow key={p.serviceId} usage={p} />
      ))}
    </Section>
  );
}
